import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSliderChange } from '@angular/material/slider';
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { VehicleData } from 'app/modules/dynamic-form/components/vehicle-capacity/vehicle-capacity.component';
import { VehicleStatusService } from '@shared/services/vehicle-status.service';
import { JobFileService } from '@core/services/job-file.service';
import { Select, Store } from '@ngxs/store';
import { WizardState } from '../../store/wizard.state';
import { Observable, Subject, of } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';

// Extend the VehicleData interface
interface ExtendedVehicleData extends VehicleData {
  selected?: boolean;
  status?: string;
  isSelected?: boolean;
  isUpdated?: boolean;
}

@Component({
    selector: 'hmt-edit-digital-twin',
    templateUrl: './edit-digital-twin.component.html',
    styleUrls: ['./edit-digital-twin.component.scss'],
    standalone: false
})
export class EditDigitalTwinComponent implements OnInit, OnDestroy {
  @Select(WizardState.getJobFile) jobFile$: Observable<any>;

  vehicles: ExtendedVehicleData[] = [];
  vehicleCategory = '';
  vehicleForm: FormGroup;
  searchQuery = '';
  filteredVehicles: ExtendedVehicleData[] = [];
  pageSize = 5;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 15, 20, 25, 100];
  displayedVehicles: ExtendedVehicleData[] = [];
  totalPages = 0;
  isStatusLoading = true;
  selectAll = false;
  someSelected = false;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<EditDigitalTwinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      vehicleType: string;
      vehicleCategory: string;
      vehicles: VehicleData[];
      // Server-paged mode (jobfile-backed): rows are fetched a page at a time from the jobfile
      // instead of being handed in as one big array, so the browser never holds the whole fleet.
      serverPaged?: boolean;
      orgId?: string;
      jobRefId?: string;
      categoryId?: string;
      // Which jobfile bucket to page: this screen renders internal and external categories as separate
      // sections, so the same categoryId must resolve to only that section's vehicles.
      ownership?: string;
    },
    private fb: FormBuilder,
    private vehicleStatusService: VehicleStatusService,
    private store: Store,
    private jobFileService: JobFileService
  ) { }

  serverPaged = false;
  /**
   * Current UI state of every row seen so far this session (vehicleId -> value), so edits survive page
   * changes. Holds ALL seen rows, not only changed ones — a row that matches the server still has to be
   * re-asserted when a category-wide select-all is part of the same save.
   */
  private seenRows = new Map<string, { isSelected: boolean; capacityUtilization: number }>();
  /**
   * The values the SERVER returned for every row seen so far (vehicleId -> value). Edits are diffed
   * against this so only what the user actually touched is sent: posting `capacityUtilization` for an
   * untouched row makes the backend stamp isUpdated=true on it, which then makes every later global
   * utilization change pop the "you have individually modified vehicles" warning.
   */
  private pristineRows = new Map<string, { isSelected: boolean; capacityUtilization: number }>();
  /** Rows in the whole category (server-reported), needed for a category-wide select-all. */
  private totalElements = 0;
  /**
   * Jobfile bucket this dialog edits: 'INTERNAL' | 'EXTERNAL' (blank = both). Derived from the section
   * the user opened, so the internal card never lists/edits the same category's external vehicles.
   */
  private ownership = '';
  /**
   * Set when "Select All Vehicles (Across All Pages)" is used in server-paged mode: the browser holds
   * one page, so the whole category is selected/cleared server-side on save. null = untouched.
   */
  private categoryWideSelection: boolean | null = null;

  ngOnInit(): void {
    this.vehicleCategory = this.data.vehicleCategory;
    this.serverPaged = !!this.data.serverPaged;
    // vehicleType already names the section ('INTERNAL' / 'EXTERNAL'); `ownership` may override it.
    const bucket = (this.data.ownership || this.data.vehicleType || '').toUpperCase();
    this.ownership = (bucket === 'INTERNAL' || bucket === 'EXTERNAL') ? bucket : '';

    if (this.serverPaged) {
      this.pageSize = 20;
      this.loadServerPage();
      return;
    }

    this.vehicles = this.data.vehicles.map(vehicle => ({
      ...vehicle,
      isSelected: vehicle.isSelected !== undefined ? vehicle.isSelected : true,
      isUpdated: vehicle.isUpdated || false
    }));
    this.filteredVehicles = [...this.vehicles];
    this.initForm();
    this.updateDisplayedVehicles();
    this.calculateTotalPages();
    this.checkVehicleStatus();
    this.updateSelectAllState();
  }

  /** Fetch the current page of vehicle rows from the JOBFILE and rebuild the page's form. */
  private loadServerPage(): void {
    this.isStatusLoading = true;
    this.jobFileService.getJobFileVehiclePage(
      this.data.orgId, this.data.jobRefId,
      this.data.categoryId || this.data.vehicleCategory || '',
      this.searchQuery || '', this.pageIndex, this.pageSize,
      this.ownership
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page: any) => {
        const rows = (page?.content || []).map((r: any) => {
          const id = String(r?.id ?? r?.vehicleNumber ?? '');
          const seen = this.seenRows.get(id);
          // Remember what the server said the first time we see a row, so later diffs stay honest even
          // after the user has edited it and come back to this page.
          if (id && !this.pristineRows.has(id)) {
            this.pristineRows.set(id, {
              isSelected: r?.isSelected !== undefined ? !!r.isSelected : true,
              capacityUtilization: r?.capacityUtilization != null ? Number(r.capacityUtilization) : 100
            });
          }
          // A pending select-all covers the pages the user never opened, so rows loaded after it must
          // show that state instead of the (not yet overwritten) server value.
          const serverSelected = r?.isSelected !== undefined ? !!r.isSelected : true;
          const selected = seen ? seen.isSelected
            : (this.categoryWideSelection !== null ? this.categoryWideSelection : serverSelected);
          return {
            id,
            vehicleId: id,
            vehicleNumber: r?.vehicleNumber,
            category: r?.categoryName,
            categoryId: r?.categoryId,
            maxLoadVolume: r?.maxLoadVolume,
            maxLoadWeight: r?.maxLoadWeight,
            costPerKM: r?.costPerKM,
            // Unsaved edits from other pages win, so navigating back shows what the user changed.
            capacityUtilization: seen?.capacityUtilization != null
              ? seen.capacityUtilization
              : (r?.capacityUtilization != null ? r.capacityUtilization : 100),
            isSelected: selected,
            isUpdated: false
          } as unknown as ExtendedVehicleData;
        });
        this.vehicles = rows;
        this.filteredVehicles = [...rows];
        this.displayedVehicles = [...rows];
        this.totalPages = page?.totalPages != null ? page.totalPages : 1;
        this.totalElements = page?.totalElements != null ? Number(page.totalElements) : rows.length;
        this.initForm();
        this.updateSelectAllState();
        this.isStatusLoading = false;
        this.checkVehicleStatus();
      },
      error: () => { this.isStatusLoading = false; }
    });
  }

  /** Snapshot the current page's state (every row, changed or not) so edits survive page changes. */
  private capturePageEdits(): void {
    const arr = this.vehicleForm?.get('vehicles') as FormArray;
    this.vehicles.forEach((v, i) => {
      const id = String((v as any).vehicleId ?? v.id ?? '');
      if (!id) { return; }
      const fg = arr ? (arr.at(i) as FormGroup) : null;
      const utilValue = fg?.get('capacityUtilization')?.value;
      const util = utilValue != null ? Number(utilValue)
        : (v.capacityUtilization != null ? Number(v.capacityUtilization) : 100);
      this.seenRows.set(id, {
        isSelected: v.isSelected !== undefined ? !!v.isSelected : true,
        capacityUtilization: util
      });
    });
  }

  /**
   * The per-row writes for a save. A field is sent only when it differs from what the server returned —
   * posting `capacityUtilization` for an untouched row makes the backend flag it as individually
   * modified, which then pops the "you have individually modified vehicles" warning on every later
   * global-utilization change.
   *
   * Exception: when a category-wide select-all runs in the same save it rewrites EVERY row in the
   * category first, so `isSelected` must be re-asserted for every row the user saw — otherwise a row
   * left at its original value (deselect all, then re-tick one vehicle) matches the server, is skipped
   * here, and the category-wide write silently wins.
   */
  private buildRowUpdates(): Array<{ vehicleId: string; isSelected?: boolean; capacityUtilization?: number }> {
    const reassertSelection = this.categoryWideSelection !== null;
    const updates: Array<{ vehicleId: string; isSelected?: boolean; capacityUtilization?: number }> = [];

    this.seenRows.forEach((row, vehicleId) => {
      const pristine = this.pristineRows.get(vehicleId);
      const selectionChanged = !pristine || pristine.isSelected !== row.isSelected;
      const utilizationChanged = !pristine
        || Number(pristine.capacityUtilization) !== Number(row.capacityUtilization);

      const isSelected = (reassertSelection || selectionChanged) ? row.isSelected : undefined;
      const capacityUtilization = utilizationChanged ? row.capacityUtilization : undefined;

      if (isSelected === undefined && capacityUtilization === undefined) { return; }
      updates.push({ vehicleId, isSelected, capacityUtilization });
    });

    return updates;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handlePageEvent(event: PageEvent): void {
    if (this.serverPaged) { this.capturePageEdits(); }
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    if (this.serverPaged) { this.loadServerPage(); return; }
    this.updateDisplayedVehicles();
    this.calculateTotalPages();
    this.updateSelectAllState();
  }

  previousPage(): void {
    if (this.pageIndex > 0) {
      if (this.serverPaged) { this.capturePageEdits(); }
      this.pageIndex--;
      if (this.serverPaged) { this.loadServerPage(); return; }
      this.updateDisplayedVehicles();
      this.updateSelectAllState();
    }
  }

  nextPage(): void {
    if (this.pageIndex < this.totalPages - 1) {
      if (this.serverPaged) { this.capturePageEdits(); }
      this.pageIndex++;
      if (this.serverPaged) { this.loadServerPage(); return; }
      this.updateDisplayedVehicles();
      this.updateSelectAllState();
    }
  }

  goToPage(page: number): void {
    if (this.serverPaged) {
      this.capturePageEdits();
      this.pageIndex = page;
      this.loadServerPage();
      return;
    }
    this.pageIndex = page;
    this.updateDisplayedVehicles();
    this.updateSelectAllState();
  }

  getPagesArray(): number[] {
    const pages = [];
    const maxVisiblePages = 9; // Show up to 9 page buttons

    if (this.totalPages <= maxVisiblePages) {
      // If we have fewer pages than the max, show all pages
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around the current page
      const currentPage = this.pageIndex + 1;
      const halfMaxPages = Math.floor(maxVisiblePages / 2);

      let startPage = currentPage - halfMaxPages;
      let endPage = currentPage + halfMaxPages;

      // Adjust if we're near the beginning or end
      if (startPage < 1) {
        endPage += (1 - startPage);
        startPage = 1;
      }

      if (endPage > this.totalPages) {
        startPage -= (endPage - this.totalPages);
        if (startPage < 1) { startPage = 1; }
        endPage = this.totalPages;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  calculateTotalPages(): void {
    if (this.serverPaged) {
      // totalPages comes from the server response; deriving it from the loaded page would give 1.
      return;
    }
    this.totalPages = Math.ceil(this.filteredVehicles.length / this.pageSize);
  }

  onVehicleSelect(index: number, event: any): void {
    // In server-paged mode the loaded arrays ARE the current page, so the row index is already global;
    // offsetting by pageIndex*pageSize would run off the end (and throw) on page 2 onwards.
    const globalIndex = this.serverPaged ? index : this.pageIndex * this.pageSize + index;
    this.displayedVehicles[index].isSelected = event.checked;
    this.filteredVehicles[globalIndex].isSelected = event.checked;
    const originalIndex = this.serverPaged
      ? globalIndex
      : this.findVehicleIndexInOriginalArray(this.filteredVehicles[globalIndex]);
    if (originalIndex !== -1) {
      this.vehicles[originalIndex].isSelected = event.checked;
    }
    this.updateSelectAllState();
  }

  onSelectAllChange(event: any): void {
    const isChecked = event.checked;
    this.selectAll = isChecked;

    // Server-paged mode holds a single page, so "across all pages" can only be honoured by the
    // backend. Record the intent and apply it to the whole category on save. With an active search the
    // checkbox keeps its legacy meaning (the listed rows only), so nothing category-wide is recorded.
    if (this.serverPaged && !(this.searchQuery && this.searchQuery.trim())) {
      this.categoryWideSelection = isChecked;
    }
    // When unchecking all, explicitly set someSelected to false
    if (!isChecked) {
      this.someSelected = false;
    }

    // Update all vehicles in the current view
    this.displayedVehicles.forEach((vehicle, index) => {
      vehicle.isSelected = isChecked;
    });

    // Update ALL filtered vehicles across all pages
    this.filteredVehicles.forEach((vehicle, index) => {
      vehicle.isSelected = isChecked;

      // Find and update in the original array
      const originalIndex = this.findVehicleIndexInOriginalArray(vehicle);
      if (originalIndex !== -1) {
        this.vehicles[originalIndex].isSelected = isChecked;
      }
    });
  }

  updateSelectAllState(): void {
    // If no filtered vehicles, set both to false
    if (this.filteredVehicles.length === 0) {
      this.selectAll = false;
      this.someSelected = false;
      return;
    }

    // Count selected vehicles
    const selectedCount = this.filteredVehicles.filter(vehicle => vehicle.isSelected).length;

    // All vehicles selected
    if (selectedCount === this.filteredVehicles.length) {
      this.selectAll = true;
      this.someSelected = false;
    }
    // No vehicles selected
    else if (selectedCount === 0) {
      this.selectAll = false;
      this.someSelected = false;
    }
    // Some vehicles selected
    else {
      this.selectAll = false;
      this.someSelected = true;
    }
  }

  onPageSizeChange(event: any): void {
    if (this.serverPaged) {
      this.capturePageEdits();
      this.pageSize = event.value;
      this.pageIndex = 0;          // a new page size restarts from the first page
      this.loadServerPage();       // refetch with the new size (totalPages comes back from the server)
      return;
    }
    this.pageSize = event.value;
    this.pageIndex = 0; // Reset to first page when changing page size
    this.updateDisplayedVehicles();
    this.calculateTotalPages();
    this.updateSelectAllState();
  }

  updateDisplayedVehicles(): void {
    if (this.serverPaged) {
      // The server already returned exactly this page — show it as-is. Never slice here: in server
      // mode `filteredVehicles` holds only the current page, so the out-of-bounds branch below would
      // see startIndex >= length on page 2+ and reset pageIndex to 0.
      this.displayedVehicles = [...this.filteredVehicles];
      return;
    }
    const startIndex = this.pageIndex * this.pageSize;
    // Reset page index if out of bounds
    if (startIndex >= this.filteredVehicles.length && this.filteredVehicles.length > 0) {
      this.pageIndex = 0;
      const newStartIndex = this.pageIndex * this.pageSize;
      this.displayedVehicles = this.filteredVehicles.slice(newStartIndex, newStartIndex + this.pageSize);
    } else {
      this.displayedVehicles = this.filteredVehicles.slice(startIndex, startIndex + this.pageSize);
    }
  }

  onSearchChange(): void {
    if (this.serverPaged) {
      // Server-side search over the whole category (not just the loaded page).
      this.capturePageEdits();
      this.pageIndex = 0;
      this.loadServerPage();
      return;
    }
    this.filterVehicles();
    this.pageIndex = 0;
    this.updateDisplayedVehicles();
    this.calculateTotalPages();
    this.updateSelectAllState();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onSearchChange();
  }

  updateCapacityUtilization(index: number, event: MatSliderChange | Event, sourceType: 'slider' | 'input'): void {
    // Validate inputs
    if (index < 0 || index >= this.displayedVehicles.length) {
      console.error('Invalid vehicle index:', index);
      return;
    }
    
    // Server-paged mode holds only the current page, so the row index is already the global one.
    const globalIndex = this.serverPaged ? index : this.pageIndex * this.pageSize + index;
    if (globalIndex < 0 || globalIndex >= this.filteredVehicles.length) {
      console.error('Invalid global index:', globalIndex);
      return;
    }
    
    // Extract the new value based on the source type
    let newValue: number;
    if (sourceType === 'slider') {
      // For range input, the event is a regular Event
      const inputEvent = event as Event;
      const inputElement = inputEvent.target as HTMLInputElement;
      newValue = +inputElement.value;
    } else {
      // For number input event, we need to access the target element
      const inputEvent = event as Event;
      const inputElement = inputEvent.target as HTMLInputElement;
      newValue = +inputElement.value;
    }

    if (newValue !== null && newValue >= 0 && newValue <= 100 && !isNaN(newValue)) {
      // Server-paged: the row's position IS its position in `vehicles`/the form array. Matching by
      // number+volume+weight would pick the first look-alike row and edit the wrong one.
      const filteredVehicleIndex = this.serverPaged
        ? globalIndex
        : this.findVehicleIndexInOriginalArray(this.filteredVehicles[globalIndex]);
      if (filteredVehicleIndex !== -1) {
        const vehiclesArray = this.vehicleForm.get('vehicles') as FormArray;
        if (!vehiclesArray || filteredVehicleIndex >= vehiclesArray.length) {
          console.error('Invalid form array or vehicle index');
          return;
        }
        
        const formGroup = vehiclesArray.at(filteredVehicleIndex) as FormGroup;
        if (!formGroup) {
          console.error('Form group not found for vehicle index:', filteredVehicleIndex);
          return;
        }
        
        // Safely get and update the capacityUtilization control
        const capacityControl = formGroup.get('capacityUtilization');
        if (capacityControl) {
          capacityControl.setValue(newValue);
        }

        // Safely handle the isUpdated control
        const isUpdatedControl = formGroup.get('isUpdated');
        if (isUpdatedControl) {
          isUpdatedControl.setValue(true);
        } else {
          const newControl = this.fb.control(true);
          formGroup.addControl('isUpdated', newControl);
        }

        // Update the data models without deep copying (which breaks component lifecycle)
        this.vehicles[filteredVehicleIndex] = {
          ...this.vehicles[filteredVehicleIndex],
          capacityUtilization: newValue,
          isUpdated: true
        };

        this.filteredVehicles[globalIndex] = {
          ...this.filteredVehicles[globalIndex],
          capacityUtilization: newValue,
          isUpdated: true
        };

        this.displayedVehicles[index] = {
          ...this.displayedVehicles[index],
          capacityUtilization: newValue,
          isUpdated: true
        };
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.serverPaged) {
      // Persist every row the user touched straight to the jobfile (page-safe: only these ids change).
      this.capturePageEdits();
      const updates = this.buildRowUpdates();

      if (this.categoryWideSelection === null && updates.length === 0) {
        this.dialogRef.close({ serverPaged: true, saved: false });
        return;
      }

      // A category-wide select-all goes first (it rewrites every row in the category); the per-row
      // edits are applied on top, so a row the user toggled after using the checkbox still wins.
      const categoryId = this.data.categoryId || this.data.vehicleCategory || '';
      const categoryWide$ = this.categoryWideSelection !== null
        ? this.jobFileService.setCategorySelectedCount(
            this.data.orgId, this.data.jobRefId, categoryId,
            this.categoryWideSelection ? this.totalElements : 0, this.ownership)
        : of(null);

      categoryWide$.pipe(
        mergeMap(() => updates.length > 0
          ? this.jobFileService.updateVehicleRows(this.data.orgId, this.data.jobRefId, updates)
          : of(null)),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => this.dialogRef.close({ serverPaged: true, saved: true }),
        error: (err) => {
          console.error('Failed to save vehicle changes to jobfile', err);
          this.dialogRef.close({ serverPaged: true, saved: false });
        }
      });
      return;
    }

    const vehiclesArray = this.vehicleForm.get('vehicles') as FormArray;
    if (!vehiclesArray) {
      console.error('Vehicles form array not found');
      return;
    }
    
    // Create a deep copy to avoid modifying read-only objects
    const updatedVehicles = JSON.parse(JSON.stringify(this.vehicles));

    // Check if any vehicle has a custom capacity utilization
    let hasCustomUtilization = false;

    this.vehicles = updatedVehicles.map((vehicle, index) => {
      const formGroup = vehiclesArray.at(index) as FormGroup;
      if (!formGroup) {
        return vehicle;
      }
      
      const capacityControl = formGroup.get('capacityUtilization');
      const isUpdatedControl = formGroup.get('isUpdated');
      
      const capacityUtilization = capacityControl ? capacityControl.value : (vehicle.capacityUtilization ?? 100);
      const isUpdated = isUpdatedControl ? isUpdatedControl.value : false;

      // Check if this vehicle has a non-default utilization
      if (capacityUtilization !== undefined && capacityUtilization !== 100) {
        hasCustomUtilization = true;
      }

      return {
        ...vehicle,
        capacityUtilization,
        isUpdated
      };
    });

    this.dialogRef.close({
      vehicles: this.vehicles,
      hasCustomUtilization
    });
  }

  private initForm(): void {
    const vehiclesFormArray = this.fb.array<FormGroup>([]);
    this.vehicles.forEach(vehicle => {
      vehiclesFormArray.push(this.createVehicleFormGroup(vehicle));
    });
    this.vehicleForm = this.fb.group({
      vehicles: vehiclesFormArray
    });
  }

  private createVehicleFormGroup(vehicle: ExtendedVehicleData): FormGroup {
    return this.fb.group({
      vehicleNumber: [vehicle.vehicleNumber],
      maxLoadVolume: [vehicle.maxLoadVolume],
      maxLoadWeight: [vehicle.maxLoadWeight],
      capacityUtilization: [vehicle.capacityUtilization ?? 100, [Validators.required, Validators.min(0), Validators.max(100)]],
      dimensions: this.fb.group({
        length: [vehicle.dimensions?.length],
        width: [vehicle.dimensions?.width],
        height: [vehicle.dimensions?.height]
      }),
      costPerKM: [vehicle.costPerKM],
      isSelected: [vehicle.isSelected !== undefined ? vehicle.isSelected : true],
      isUpdated: [vehicle.isUpdated || false]
    });
  }

  private filterVehicles(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredVehicles = [...this.vehicles];
    } else {
      const searchTerm = this.searchQuery.toLowerCase().trim();
      this.filteredVehicles = this.vehicles.filter(vehicle => {
        const vehicleNumber = String(vehicle.vehicleNumber).toLowerCase();
        return vehicleNumber.includes(searchTerm);
      });
    }
  }

  private findVehicleIndexInOriginalArray(filteredVehicle: ExtendedVehicleData): number {
    if (!filteredVehicle) {
      return -1;
    }
    
    return this.vehicles.findIndex(v =>
      v && v.vehicleNumber === filteredVehicle.vehicleNumber &&
      v.maxLoadVolume === filteredVehicle.maxLoadVolume &&
      v.maxLoadWeight === filteredVehicle.maxLoadWeight
    );
  }

  private checkVehicleStatus(): void {
    if (!this.vehicles || this.vehicles.length === 0) {
      this.isStatusLoading = false;
      return;
    }
    
    this.isStatusLoading = true;
    const vehicleIds = this.vehicles.map(v => String(v.id)).filter(id => id && id !== 'undefined');
    
    if (vehicleIds.length === 0) {
      this.isStatusLoading = false;
      return;
    }
    
    const jobFile = this.store.selectSnapshot(WizardState.getJobFile);
    const orgId = jobFile?.orgId || '';
    
    this.vehicleStatusService.getVehicleStatus(vehicleIds, orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response && Array.isArray(response)) {
            this.vehicles = this.vehicles.map(vehicle => ({
              ...vehicle,
              status: response.includes(String(vehicle.id)) ? 'On the Job' : 'Off the Job'
            }));
            this.filteredVehicles = [...this.vehicles];
            this.updateDisplayedVehicles();
          }
          this.isStatusLoading = false;
        },
        (error) => {
          console.error('Error fetching vehicle status:', error);
          this.isStatusLoading = false;
        }
      );
  }
}
